/**
 * Copyright (c) 2025 Stefan Radig
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

/**
 * Write token separator used in URLs
 * Format: documentId-wt_writeToken
 */
const WRITE_TOKEN_SEPARATOR = '-wt_';

/**
 * Append-only token separator used in URLs
 * Format: documentId-ao_appendOnlyToken
 */
const APPEND_ONLY_TOKEN_SEPARATOR = '-ao_';

/**
 * Parse a URL hash that may contain a write token and/or append-only token
 * @param hash - The URL hash value (without #) e.g. "docId-wt_token", "docId-ao_token", "docId-wt_token-ao_token", or just "docId"
 * @returns { documentId: string, writeToken: string | null, appendOnlyToken: string | null }
 */
export function parseUrlFragment(hash: string): { documentId: string; writeToken: string | null; appendOnlyToken: string | null } {
  let documentId = hash;
  let writeToken: string | null = null;
  let appendOnlyToken: string | null = null;

  // Check for write token
  const wtIndex = hash.indexOf(WRITE_TOKEN_SEPARATOR);
  if (wtIndex !== -1) {
    documentId = hash.substring(0, wtIndex);
    const afterWt = hash.substring(wtIndex + WRITE_TOKEN_SEPARATOR.length);

    // Check if there's also an append-only token after the write token
    const aoInAfterWt = afterWt.indexOf(APPEND_ONLY_TOKEN_SEPARATOR);
    if (aoInAfterWt !== -1) {
      writeToken = afterWt.substring(0, aoInAfterWt);
      appendOnlyToken = afterWt.substring(aoInAfterWt + APPEND_ONLY_TOKEN_SEPARATOR.length);
    } else {
      writeToken = afterWt;
    }
    return { documentId, writeToken, appendOnlyToken };
  }

  // Check for append-only token (without write token)
  const aoIndex = hash.indexOf(APPEND_ONLY_TOKEN_SEPARATOR);
  if (aoIndex !== -1) {
    documentId = hash.substring(0, aoIndex);
    appendOnlyToken = hash.substring(aoIndex + APPEND_ONLY_TOKEN_SEPARATOR.length);
    return { documentId, writeToken: null, appendOnlyToken };
  }

  return { documentId: hash, writeToken: null, appendOnlyToken: null };
}

/**
 * Create a URL hash with write token
 * @param documentId - The document ID
 * @param writeToken - The write token (optional)
 * @returns URL hash string (without #) e.g. "docId-wt_token" or "docId"
 */
export function createUrlWithWriteToken(documentId: string, writeToken?: string | null): string {
  if (writeToken) {
    return documentId + WRITE_TOKEN_SEPARATOR + writeToken;
  }
  return documentId;
}

/**
 * Create a URL hash with write token and append-only token
 * @param documentId - The document ID
 * @param writeToken - The write token (optional)
 * @param appendOnlyToken - The append-only token (optional)
 * @returns URL hash string (without #) e.g. "docId-wt_token-ao_token" or "docId-wt_token" or "docId"
 */
export function createUrlWithTokens(documentId: string, writeToken?: string | null, appendOnlyToken?: string | null): string {
  let url = documentId;
  if (writeToken) {
    url += WRITE_TOKEN_SEPARATOR + writeToken;
  }
  if (appendOnlyToken) {
    url += APPEND_ONLY_TOKEN_SEPARATOR + appendOnlyToken;
  }
  return url;
}

/**
 * Append write token to a file ID for server requests
 * @param fileId - The base file ID (32-char hex)
 * @param writeToken - The write token (optional)
 * @returns File ID with write token appended if provided
 */
export function appendWriteToken(fileId: string, writeToken?: string | null): string {
  if (writeToken) {
    return fileId + WRITE_TOKEN_SEPARATOR + writeToken;
  }
  return fileId;
}

/**
 * Append append-only token to a file ID for server requests
 * @param fileId - The base file ID (32-char hex)
 * @param appendOnlyToken - The append-only token (optional)
 * @returns File ID with append-only token appended if provided
 */
export function appendAppendOnlyToken(fileId: string, appendOnlyToken?: string | null): string {
  if (appendOnlyToken) {
    return fileId + APPEND_ONLY_TOKEN_SEPARATOR + appendOnlyToken;
  }
  return fileId;
}

/**
 * Create a fast-format URL hash: appType:docId[-wt_token][-ao_token]
 * This is the new simplified URL format.
 *
 * @param appType - The app type (e.g., "drive", "doc", "gate")
 * @param documentId - The document ID (32 hex chars)
 * @param writeToken - Optional write token for full access
 * @param appendOnlyToken - Optional append-only token
 * @returns URL hash string (without #) e.g. "drive:abc123-wt_token"
 */
export function createFastUrl(
  appType: string,
  documentId: string,
  writeToken?: string | null,
  appendOnlyToken?: string | null
): string {
  let url = `${appType}:${documentId}`;
  if (writeToken) {
    url += WRITE_TOKEN_SEPARATOR + writeToken;
  }
  if (appendOnlyToken) {
    url += APPEND_ONLY_TOKEN_SEPARATOR + appendOnlyToken;
  }
  return url;
}

/**
 * Create a full shareable URL with the new simplified format.
 * URL format: https://origin#appType:docId[-wt_token]
 *
 * @param appType - The app type (e.g., "drive", "doc", "gate")
 * @param documentId - The document ID (32 hex chars)
 * @param writeToken - Optional write token for full access
 * @param appendOnlyToken - Optional append-only token
 * @param origin - Optional origin (defaults to window.location.origin)
 * @returns Full URL string e.g. "https://editoor.radig.com#drive:abc123"
 */
export function createFullUrl(
  appType: string,
  documentId: string,
  writeToken?: string | null,
  appendOnlyToken?: string | null,
  origin?: string
): string {
  const baseOrigin = origin || window.location.origin;
  const hash = createFastUrl(appType, documentId, writeToken, appendOnlyToken);
  return `${baseOrigin}#${hash}`;
}